import React from 'react';
import type { InspectorTab } from '../types';
import { useAppSelector } from '@state/store';
import { WindInspector } from './inspectors/WindInspector';
import { DomainInspector } from './inspectors/DomainInspector';
import { BoundariesInspector } from './inspectors/BoundariesInspector';
import { StudyOverviewInspector } from './inspectors/StudyOverviewInspector';
import { RunQueueInspector } from './inspectors/RunQueueInspector';
import { GeometryInspector } from './inspectors/GeometryInspector';
import { validateCfdSetup } from '../validation/cfdValidation';

export interface InspectorPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  activeTab: InspectorTab;
  onTabChange: (tab: InspectorTab) => void;
  selectedNodeId?: string;
}

const INSPECTOR_TABS: { id: InspectorTab; label: string }[] = [
  { id: 'properties', label: 'Properties' },
  { id: 'visualization', label: 'Visualization' },
  { id: 'data', label: 'Data' }
];

export const InspectorPanel: React.FC<InspectorPanelProps> = ({
  isOpen,
  onToggle,
  activeTab,
  onTabChange,
  selectedNodeId = 'study-overview'
}) => {
  const activeScenarioId = useAppSelector((state) => state.study.study.activeScenarioId);
  const activeScenario = useAppSelector(
    (state) => state.study.study.scenarios[activeScenarioId]
  );
  const geometryCount = useAppSelector((state) => state.geometry.ids.length);

  const validation = validateCfdSetup(activeScenario?.cfdSetup, geometryCount);

  if (!isOpen) {
    return (
      <aside className="ssw-inspector-collapsed" aria-label="Inspector Panel">
        <button
          type="button"
          className="ssw-panel-expand-btn"
          onClick={onToggle}
          aria-label="Expand Inspector panel"
          title="Expand Inspector (Right Panel)"
        >
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M4 4H12V12H4V4Z" />
            <path d="M4 7H12" />
          </svg>
          <span className="ssw-collapsed-panel-label">INSPECTOR</span>
        </button>
      </aside>
    );
  }

  const selectedGeometryId = useAppSelector((state) => state.geometry.selectedId);

  const renderPropertiesContent = () => {
    // If a block/geometry is selected in the viewport, show its properties on the right
    if (selectedGeometryId) {
      return <GeometryInspector />;
    }
    if (selectedNodeId === 'site-wind') {
      return <WindInspector />;
    }
    if (selectedNodeId === 'site-domain') {
      return <DomainInspector />;
    }
    if (selectedNodeId === 'site-boundaries') {
      return <BoundariesInspector />;
    }
    if (selectedNodeId === 'run-queue') {
      return <RunQueueInspector />;
    }
    if (selectedNodeId === 'site-buildings' || selectedNodeId === 'site-geometry') {
      return <GeometryInspector />;
    }
    return <StudyOverviewInspector />;
  };

  const renderVisualizationContent = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '11.5px' }}>
        <div style={{ borderBottom: '1px solid rgba(47, 120, 196, 0.12)', paddingBottom: '8px' }}>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '9px',
              fontWeight: 700,
              color: '#2F78C4',
              letterSpacing: '0.12em',
              marginBottom: '2px'
            }}
          >
            VIEWPORT PRESENTATION LAYERS
          </div>
          <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
            Gate 1 Spatial Layers
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div
            style={{
              padding: '8px 10px',
              borderRadius: '4px',
              border: '1px solid rgba(47, 120, 196, 0.16)',
              background: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>Computational Domain</div>
              <div style={{ fontSize: '9.5px', color: '#64748b' }}>Transparent 3D bounding extents</div>
            </div>
            <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600 }}>VISIBLE</span>
          </div>

          <div
            style={{
              padding: '8px 10px',
              borderRadius: '4px',
              border: '1px solid rgba(47, 120, 196, 0.16)',
              background: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>Prescribed Wind Indicator</div>
              <div style={{ fontSize: '9.5px', color: '#64748b' }}>Incoming velocity direction arrow</div>
            </div>
            <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600 }}>VISIBLE</span>
          </div>

          <div
            style={{
              padding: '8px 10px',
              borderRadius: '4px',
              border: '1px solid rgba(47, 120, 196, 0.16)',
              background: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>Site Geometry</div>
              <div style={{ fontSize: '9.5px', color: '#64748b' }}>Extruded building obstacle masses</div>
            </div>
            <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600 }}>VISIBLE ({geometryCount})</span>
          </div>
        </div>

        <div
          style={{
            borderLeft: '3px solid #d97706',
            padding: '8px 10px',
            background: 'rgba(217, 119, 6, 0.05)',
            borderRadius: '0 3px 3px 0',
            fontSize: '9.5px',
            color: '#78350f',
            lineHeight: 1.4
          }}
        >
          <strong>NO FAKE SCIENCE RULE:</strong>
          <br />
          Streamlines, particle traces, and velocity contour fields are strictly unrendered until an authoritative
          CFD solver result is received from a connected engine.
        </div>
      </div>
    );
  };

  const renderDataContent = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '11.5px' }}>
        <div style={{ borderBottom: '1px solid rgba(47, 120, 196, 0.12)', paddingBottom: '8px' }}>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '9px',
              fontWeight: 700,
              color: '#2F78C4',
              letterSpacing: '0.12em',
              marginBottom: '2px'
            }}
          >
            STUDY STATE SPECIFICATION
          </div>
          <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
            State Inspector
          </div>
        </div>

        <div style={{ fontSize: '10.5px', color: '#475569' }}>
          <strong>UI-defined State vs Engine-ready State:</strong>
          <div style={{ marginTop: '4px', fontSize: '10px', color: '#64748b' }}>
            Status: <strong>{validation.statusLabel}</strong> ({validation.isReady ? 'All parameters complete' : `${validation.missingCount} missing`})
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '9.5px', fontWeight: 600, color: '#334155' }}>
            DURABLE CFD SETUP (JSON):
          </span>
          <pre
            style={{
              margin: 0,
              padding: '8px',
              background: '#ffffff',
              border: '1px solid rgba(47, 120, 196, 0.2)',
              borderRadius: '3px',
              fontSize: '9.5px',
              fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
              color: '#1e3a8a',
              maxHeight: '260px',
              overflowY: 'auto'
            }}
          >
            {JSON.stringify(activeScenario?.cfdSetup, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <aside className="ssw-inspector-panel" aria-label="Inspector Panel">
      {/* Panel Header */}
      <div className="ssw-panel-header">
        <div className="ssw-panel-title-lockup">
          <h2 className="ssw-panel-title">Inspector</h2>
        </div>
        <div className="ssw-panel-header-actions">
          <button
            type="button"
            className="ssw-panel-tool-btn"
            onClick={onToggle}
            aria-label="Collapse Inspector panel"
            title="Collapse panel"
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M6 3L11 8L6 13" />
            </svg>
          </button>
        </div>
      </div>

      {/* Structural Tabs */}
      <div className="ssw-panel-tabs" role="tablist" aria-label="Inspector Tabs">
        {INSPECTOR_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`ssw-inspector-tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`ssw-inspector-content-${tab.id}`}
              className={`ssw-panel-tab-btn ${isActive ? 'is-active' : ''}`}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div
        className="ssw-panel-body"
        role="tabpanel"
        id={`ssw-inspector-content-${activeTab}`}
        aria-labelledby={`ssw-inspector-tab-${activeTab}`}
        style={{ padding: '14px 16px', overflowY: 'auto' }}
      >
        {activeTab === 'properties' && renderPropertiesContent()}
        {activeTab === 'visualization' && renderVisualizationContent()}
        {activeTab === 'data' && renderDataContent()}
      </div>
    </aside>
  );
};

export default InspectorPanel;
